import { createApp as createVueApp, h, createSSRApp } from 'vue';
import { createHead } from '@unhead/vue';
import { SitecoreJssPlaceholderPlugin } from '@sitecore-jss/sitecore-jss-vue';
import { DefaultApolloClient } from '@vue/apollo-composable';
import AppRoot from './AppRoot';
import { createRouter } from './router';
import SitecoreJssStorePlugin from './lib/SitecoreJssStorePlugin';
import GraphQLClientFactory from './lib/GraphQLClientFactory';
import config from './temp/config';
import componentFactory from './temp/componentFactory';
import { createGtm } from '@gtm-support/vue-gtm';
import { createVfm } from 'vue-final-modal';
import 'vue-final-modal/style.css';
import VueSplide from '@splidejs/vue-splide';
import { openInNewTab } from './routeUtils';

// createApp is invoked by both the main and SSR entry points, so the two entry points can use the same app creation process.
export function createApp(initialState, isSSR, ctx) {
  const router = createRouter(isSSR);
  const graphQLProvider = createGraphQLProvider(initialState);

  // Cancel the navigation if the new route should be opened in a new tab.
  // Used for the XML '/feed/*' URLs, which can't be client-side rendered
  // in the Vue app.
  router.beforeEach((newRoute, oldRoute) => {
    if (typeof window !== 'undefined') {
      if (openInNewTab(newRoute)) {
        window?.open(newRoute.fullPath, '_blank');
        return false;
      }
    }
  });

  const vueOptions = {
    router,
    render: () => h(AppRoot)
  };

  const app = isSSR ? createSSRApp(vueOptions) : createVueApp(vueOptions);

  app.provide(DefaultApolloClient, graphQLProvider);

  app.use(router);
  app.use(SitecoreJssStorePlugin);
  app.use(SitecoreJssPlaceholderPlugin, { componentFactory });

  const head = isSSR ? ctx.head : createHead();
  app.use(head);
  const vfm = createVfm();
  app.use(vfm);
  app.use(VueSplide);

  // if there is an initial state defined, push it into the store, where it can be referenced by interested components.
  if (initialState) {
    app.config.globalProperties.$jss.store.setSitecoreData(initialState);
  }

  // Google Tag Manager Support
  const $domain = config.sitecoreApiHost;
  // Always Add Konetiq GTM ID
  var $gtmids = ['GTM-NHVHFLL7'];

  switch ($domain) {
    case 'https://www.cisecurity.org':
      $gtmids.push('GTM-P5Q9JG6');
      break;
    case 'https://www.codemerge.dev.cisecurity.org':
    case 'https://www.codemerge.qa.cisecurity.org':
    case 'https://cm.cisecurity.localhost':
    case 'http://localhost:3000':
      $gtmids.push('GTM-TS8TCNR');
      break;
  }

  app.use(
    createGtm({
      id: $gtmids,
      defer: false,
      compatibility: false,
      enabled: true,
      debug: false,
      loadScript: true,
      vueRouter: router,
      trackOnNextTick: false
    })
  );

  return { app, router, graphQLProvider, ctx };
}
export function createGraphQLProvider(initialState) {
  const client =
    initialState && initialState.APOLLO_STATE
      ? GraphQLClientFactory(config.graphQLEndpoint, false, initialState.APOLLO_STATE)
      : GraphQLClientFactory(config.graphQLEndpoint, true);

  return client;
}
